import React, {useEffect, useState} from 'react';
import {Button, Grid, List, ListItem, Typography, makeStyles} from '@material-ui/core';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import PrintIcon from '@material-ui/icons/Print';
import {AgeniumQuestionTranscript, getTranscript} from '../../../APIs/Agenium/api';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import FixedNavigation from './common/FixedNavigation';

interface DecisionDeclinedProps {
    onExit: () => void;
    onNext: () => void;
    parameterStore: ParameterStore;
    ageniumContext: AgenuimContextType;
}
const useStyles = makeStyles(() => ({
    noPrint: {
        '@media print': {
            display: 'none !important',
        },
    },
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#456d69',
        marginBottom: '28px',
    },
    text: {
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    smallText: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#838383',
        marginBottom: '18px',
    },
    red: {
        color: '#f65858',
    },
    list: {
        fontFamily: 'FredokaRegular',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#f65858',
        listStyle: 'disc',
        paddingLeft: '20px',
        marginBottom: '18px',
        '& .MuiListItem-root': {
            display: 'list-item',
            paddingLeft: '4px',
        },
    },
    p1: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    printBtn: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '28.8px',
        color: '#434343',
        textTransform: 'none',
    },
    downloadBtn: {
        fontFamily: 'FredokaRegular',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '25.6px',
        textTransform: 'none',
        marginTop: '30px',
        marginLeft: '18px',
    },
    navigation: {
        marginTop: '20px',
    },
    backBtn: {
        borderRadius: '20px',
    },
    nextBtn: {
        minWidth: '400px',
        borderRadius: '20px',
    },
    printDialog: {
        marginTop: '30px',
        borderRadius: 20,
        border: '1px solid #888',
        padding: '20px 40px',
        boxShadow: '2px 2px 2px 2px #ccc',
        '@media print': {
            marginTop: 0,
            border: 'none',
            boxShadow: 'none',
            maxWidth: '100%',
            flexBasis: '100%',
            '& .ai_question': {
                padding: 2,
                boxShadow: '1px 1px 1px 2px #ccc',
            },
        },
        '& .ai_answer': {
            paddingBottom: '14px',
            borderBottom: '1px solid #ccc',
        },
        '& .ai_answer:last-child': {
            borderBottom: 'none',
        },
    },
}));

const DecisionDeclined: React.FC<DecisionDeclinedProps> = ({onExit, onNext, parameterStore, ageniumContext}) => {
    const classes = useStyles();
    const [showQA, setShowQA] = useState(false);
    const [listQA, setListQA] = useState<AgeniumQuestionTranscript[]>([]);

    useEffect(() => {
        ageniumContext.initSdk();
    }, []);

    const declineCancelReasonMap = new Map<string, string>([
        ['DACLAN_001', 'Medical Underwriting'],
        ['DACLAN_002', 'Non-Medical Underwriting'],
        ['DAEIRD', 'Third Party Data: LexisNexis'],
        ['DACLAN_001', 'Medical Information disclosed'],
        ['DACLAN_002', 'Non Medical Information disclosed'],
        ['DLPSTNR_001', 'Postponed 3 months'],
        ['DLPSTNR_002', 'Postponed 6 months'],
        ['DLPSTNR_003', 'Postponed 12 months'],
        ['DLPSTNR_004', 'Postponed 24months'],
        ['DMIBDL_001', 'Medical MIB'],
        ['DMIBDL_002', 'Non-Medical MIB'],
        ['DACLAD', 'Milliman Report'],
        ['DAEIRD', 'LexisNexis Electronic Inspection'],
        ['DAMVRD', 'LexisNexis Motor Vehicle Report'],
        ['DDCLAB', 'Lab Results'],
        ['DDCLN', 'General Decline'],
        ['CPRNNP', 'Cancelled'],
        ['CINLNP', 'Ineligible'],
        ['CRCFNP', 'Withdrawn'],
    ]);
    const declineReasonText = function () {
        var declineKey =
            ageniumContext.applicationData?.find((t) => t.key.includes('Case_UWDecision_Letters_Overall'))?.value ??
            parameterStore.prosperDeclineReason;
        var declineReason = declineCancelReasonMap.get(declineKey);
        return declineKey + ' ' + declineReason;
    };

    return (
        <Grid container classes={{root: classes.wrapper}}>
            <Typography classes={{root: `${classes.title} ${classes.noPrint}`}} data-testId="decisionDeclined">
                Your Client Was{' '}
                <Typography component="span" classes={{root: `${classes.title} ${classes.red}`}}>
                    Denied.
                </Typography>
            </Typography>
            <Grid container>
                <Grid item xs={9} classes={{root: classes.noPrint}}>
                    <Typography classes={{root: classes.text}}>Additional Details</Typography>
                    <Typography classes={{root: classes.smallText}}>
                        Details about your client’s application status.
                    </Typography>
                    <List disablePadding classes={{root: classes.list}}>
                        <ListItem>
                            Decision:{' '}
                            {ageniumContext.applicationData?.find((t) => t.key === 'Case_UWDecision_Overall')?.value ??
                                parameterStore.prosperDecision}
                        </ListItem>
                        <ListItem>Reason: {declineReasonText()}</ListItem>
                    </List>
                    <Typography classes={{root: classes.p1}}>
                        But that’s okay! Let’s get them a quote for a new product type. Ready to jump back to the quotes
                        page? Click the button below to get started. Don’t worry, all of the client’s information will
                        be prefilled!
                    </Typography>
                    {(!listQA.length as any) > 0 && (
                        <Button
                            color="primary"
                            variant="text"
                            onClick={async () => {
                                if (parameterStore.applicationId) {
                                    setShowQA(true);
                                    setListQA(await getTranscript(parameterStore.applicationId));
                                }
                            }}
                            startIcon={<SaveAltIcon />}
                            classes={{root: classes.downloadBtn}}
                            disabled={!parameterStore.applicationId || showQA}
                        >
                            {showQA ? 'Loading...' : 'Download Application Info'}
                        </Button>
                    )}
                </Grid>
                {showQA && listQA.length > 0 && (
                    <Grid item xs={9} classes={{root: classes.printDialog}}>
                        <Grid container>
                            <Grid item xs={10}>
                                <Typography classes={{root: classes.p1}}>Application Info</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Button
                                    classes={{root: `${classes.printBtn} ${classes.noPrint}`}}
                                    startIcon={<PrintIcon />}
                                    onClick={() => window.print()}
                                >
                                    Print
                                </Button>
                            </Grid>
                        </Grid>
                        {listQA.map(
                            (question) =>
                                typeof question?.question != 'undefined' &&
                                typeof question?.answer != 'undefined' && (
                                    <>
                                        <p className="ai_question">
                                            Question:{' '}
                                            {parse(
                                                DOMPurify.sanitize(question?.question.replaceAll('&nbsp;', ' '), {
                                                    USE_PROFILES: {html: true},
                                                }),
                                            )}
                                        </p>
                                        <p className="ai_answer">
                                            Answer:{' '}
                                            {parse(
                                                DOMPurify.sanitize(question?.answer, {
                                                    USE_PROFILES: {html: true},
                                                }),
                                            )}
                                        </p>
                                    </>
                                ),
                        )}
                    </Grid>
                )}
            </Grid>
            <FixedNavigation onNextCustomText="Return to Quotes" onNext={onNext} isLoading={false} />
        </Grid>
    );
};

export default DecisionDeclined;
